import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Divider, Dropdown, Grid, Header, Modal, Radio } from 'semantic-ui-react'
import { find, map } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { v4 } from 'uuid'
import SuperTable from './SuperTable'
import { constants } from '../../constants'
import { dynamicChannelsActions } from '../../actions/dynamicChannelsActions'
import classNames from 'classnames'

const propTypes = {
    isOpen: PropTypes.bool,
    trigger: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.node]),
    title: PropTypes.string,
    description: PropTypes.string,
    closeCaption: PropTypes.string,
    confirmCaption: PropTypes.string,
    onClose: PropTypes.func,
    onLinkGroups: PropTypes.func,
}

const defaultProps = {
    isOpen: false,
    trigger: null,
    title: null, // Will use translation key
    description: null, // Will use translation key
    closeCaption: null, // Will use translation key
    confirmCaption: null, // Will use translation key
    onClose: () => {},
    onLinkGroups: () => {},
}

const LinkGroupsModal = (props) => {
    const {
        isOpen,
        trigger,
        title,
        description,
        closeCaption,
        confirmCaption,
        onClose,
        onLinkGroups
    } = props

    const { t } = useTranslation()
    const dispatch = useDispatch()
    const community = useSelector(state => state.navigation.community)
    const dynamicChannelsState = useSelector(state => state.dynamicChannels)
    
    const [open, setOpen] = useState(isOpen)
    const [selectedEntityType, setSelectedEntityType] = useState('parent')
    const [selectedChannelTypeId, setSelectedChannelTypeId] = useState('')
    const [selectedGroups, setSelectedGroups] = useState([])
    const [tableId, setTableId] = useState(v4())

    // Get data from Redux store
    const { 
        channelTypes, 
        channelInstances, 
        loading, 
        typesLoading,
        instancesLoading,
        errors 
    } = dynamicChannelsState

    // Entity type options for Step 1 (aligned with d6+ API)
    const entityTypeOptions = [
        { key: 'parent', value: 'parent', text: t('linkGroups.entityTypes.parent') },
        { key: 'learner', value: 'learner', text: t('linkGroups.entityTypes.learner') },
        { key: 'staff_member', value: 'staff_member', text: t('linkGroups.entityTypes.staffMember') }
    ]

    // Dynamic channel type options from Redux store
    const channelTypeOptions = useMemo(() => {
        const options = [
            { key: '', value: '', text: t('linkGroups.channelTypes.selectOne') }
        ]
        
        if (channelTypes && channelTypes.length > 0) {
            channelTypes.forEach(type => {
                options.push({
                    key: type.key,
                    value: type.value,
                    text: type.text
                })
            })
        }
        
        return options
    }, [channelTypes, t])

    // Table headers for results
    const headers = [
        {
            name: constants.TABLE_KEY_CHECKBOX,
            key: {
                name: constants.TABLE_KEY_CHECKBOX,
                type: constants.TABLE_KEY_CHECKBOX,
            }
        },
        {
            name: t('linkGroups.table.headers.name'),
            classNames: 'channel-name',
            sortable: false,
            key: {
                name: "name",
                classNames: 'channel-name',
            }
        },
        {
            name: '',
            classNames: 'link-status',
            align: "right",
            key: {
                name: "status",
                classNames: 'link-status',
                align: "right"
            }
        },
        {
            name: "",
            key: {
                classNames: 'actions',
                name: constants.TABLE_KEY_ACTIONS,
                type: constants.TABLE_KEY_ACTIONS
            },
        }
    ]

    // Fetch channel types when entity type changes
    useEffect(() => {
        if (selectedEntityType && community?.id) {
            dispatch(dynamicChannelsActions.startTypes())
            dispatch(dynamicChannelsActions.fetchChannelTypes({
                communityId: community.id,
                entityType: selectedEntityType
            }))
        }
    }, [selectedEntityType, community?.id, dispatch])

    // Fetch channel instances when channel type changes
    useEffect(() => {
        if (selectedEntityType && selectedChannelTypeId && community?.id) {
            dispatch(dynamicChannelsActions.startInstances())
            dispatch(dynamicChannelsActions.fetchChannelInstances({
                communityId: community.id,
                entityType: selectedEntityType,
                channelTypeId: selectedChannelTypeId
            }))
        }
    }, [selectedEntityType, selectedChannelTypeId, community?.id, dispatch])

    const handleCloseModal = () => {
        setOpen(false)
        onClose()
    }

    const handleLinkGroups = () => {
        const groupsToLink = selectedGroups.filter(groupId => {
            const group = find(channelInstances, { id: groupId })
            return group && !group.is_linked
        })

        if (groupsToLink.length === 0) {
            handleCloseModal()
            return
        }

        dispatch(dynamicChannelsActions.startInstances())
        dispatch(dynamicChannelsActions.createDynamicChannels({
            communityId: community.id,
            entityType: selectedEntityType,
            channelTypeId: selectedChannelTypeId,
            channelValues: groupsToLink
        })).finally(() => {
            onLinkGroups(groupsToLink)
        })
    }

    const handleEntityTypeChange = (e, { value }) => {
        setSelectedEntityType(value)
        setSelectedChannelTypeId('')
        setSelectedGroups([])
    }

    const handleChannelTypeChange = (e, { value }) => {
        setSelectedChannelTypeId(value)
        setSelectedGroups([])
    }

    const handleCheckUpdate = (records) => {
        setSelectedGroups(records)
    }

    const onModalUnmount = () => {
        setSelectedEntityType('parent')
        setSelectedChannelTypeId('')
        setSelectedGroups([])
        dispatch(dynamicChannelsActions.clearChannelData())
    }

    const onModalMount = () => {
        // Reset state when modal opens
        setSelectedEntityType('parent')
        setSelectedChannelTypeId('')
        setSelectedGroups([])
    }

    const { hasResults, hasSelection, canLinkGroups } = useMemo(() => {
        const hasResults = channelInstances && channelInstances.length > 0
        const hasSelection = selectedGroups.length > 0
        const canLinkGroups = hasSelection && selectedGroups.some(groupId => {
            const group = find(channelInstances, { id: groupId })
            return group && !group.is_linked
        })
        return { hasResults, hasSelection, canLinkGroups }
    }, [channelInstances, selectedGroups])

    useEffect(() => {
        // When we change the filters, we should update our default selection to all the groups
        setSelectedGroups(channelInstances.map(group => String(group.id)))
        setTableId(v4()) // We are forcing out the table to re-render
    }, [channelInstances])

    return (
        <Modal
            size="large"
            open={isOpen}
            trigger={trigger}
            onClose={handleCloseModal}
            onUnmount={onModalUnmount}
            onMount={onModalMount}
            onOpen={() => setOpen(true)}>

            <Modal.Content className="link-groups-modal no-padding">
                <Grid className='mg-auto'>
                    <Grid.Row columns={1}>
                        <Grid.Column>
                            <Header as="h2">{title || t('linkGroups.modal.title')}</Header>
                            <p>{description || t('linkGroups.modal.description')}</p>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>

                <Divider />

                <Grid className='mg-auto'>
                    <Grid.Row columns={2}>
                        <Grid.Column width={8}>
                            <Header as="h3">{t('linkGroups.steps.step1')}</Header>
                            <Header as="h4">{t('linkGroups.steps.whoToLink')}</Header>
                            
                            <div style={{ marginTop: '16px' }}>
                                {entityTypeOptions.map(option => (
                                    <div key={option.key} style={{ display: 'inline-block', marginRight: '24px' }}>
                                        <Radio
                                            name="entityType"
                                            label={option.text}
                                            value={option.value}
                                            checked={selectedEntityType === option.value}
                                            onChange={handleEntityTypeChange}
                                        />
                                    </div>
                                ))}
                            </div>
                        </Grid.Column>

                        <Grid.Column width={4}>
                            <Header as="h3">{t('linkGroups.steps.step2')}</Header>
                            <div className="input-container theme-color">
                                <label className='label' htmlFor="channel-type">{t('linkGroups.labels.channelType')}</label>
                                <Dropdown
                                    id="channel-type"
                                    placeholder={t('linkGroups.channelTypes.selectOne')}
                                    fluid
                                    selection
                                    loading={typesLoading}
                                    disabled={typesLoading}
                                    value={selectedChannelTypeId}
                                    options={channelTypeOptions}
                                    onChange={handleChannelTypeChange}
                                />
                            </div>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>

                <Grid className='mg-auto' style={{ marginTop: '32px' }}>
                    <Grid.Row columns={1}>
                        <Grid.Column>
                            <Header as="h3">{t('linkGroups.results.title')}</Header>
                            
                            <div className="table-container">
                                <SuperTable
                                    id={tableId}
                                    key={tableId}
                                    classNames={['super-t__link-groups']}
                                    loading={typesLoading || instancesLoading}
                                    headers={headers}
                                    selection={selectedGroups}
                                    keys={map(headers, "key")}
                                    records={channelInstances || []}
                                    onRecordClick={() => {}}
                                    noRecordsMessage={(!hasResults && !(typesLoading || instancesLoading) && (selectedEntityType && selectedChannelTypeId)) ? t('linkGroups.results.emptyMessage') : t('linkGroups.table.noRecordsMessage')}
                                    onCheckUpdate={handleCheckUpdate}
                                    actionFunctions={{}}
                                />
                            </div>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            </Modal.Content>

            <Modal.Actions className={classNames("no-border", instancesLoading ? "no-select op-60" : "")}>
                <Button className="secondary" onClick={handleCloseModal}>
                    {closeCaption || t('linkGroups.buttons.dismiss')}
                </Button>
                <Button 
                    className={hasSelection ? "primary" : "primary disabled"}
                    disabled={!hasSelection}
                    onClick={handleLinkGroups}>
                    {confirmCaption || t('linkGroups.buttons.linkGroups')}
                </Button>
            </Modal.Actions>
        </Modal>
    )
}

LinkGroupsModal.propTypes = propTypes
LinkGroupsModal.defaultProps = defaultProps

export default LinkGroupsModal
